import { useEffect, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type { EnrollmentModeQuoteResponse } from "@bw/shared/types/generated";

const blockers: Record<string, string> = {
  commercial_binding_unavailable:
    "Current payment terms are unavailable. Ask the club to review its contract, then refresh.",
  commercial_fee_edition_unavailable:
    "Fee terms are unavailable. Ask the club to review its fee edition, then refresh.",
  commercial_routing_unavailable:
    "The club cannot take card payments yet. Contact the club or try again later.",
  commercial_source_unavailable:
    "This enrollment review is unavailable. Refresh to review the current class.",
  campaign_stacking_unavailable:
    "This class special is not available with the club’s current payment terms.",
};

export function hasReadyCommercialReview(
  quote: EnrollmentModeQuoteResponse | undefined,
  now = Date.now(),
): boolean {
  const review = quote?.commercial_fee;
  if (
    !quote?.quote_id ||
    !review ||
    review.readiness !== "ready" ||
    !review.fee_quote_id ||
    !review.bw_commission ||
    !review.acquisition_fee ||
    review.blocker_codes.length ||
    !review.resolved_at ||
    !review.valid_until
  )
    return false;
  const uuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const nonzero = (value: string) =>
    uuid.test(value) && value !== "00000000-0000-0000-0000-000000000000";
  const money = (value: NonNullable<typeof review.bw_commission>) =>
    Number.isSafeInteger(value.amount_minor) &&
    value.amount_minor >= 0 &&
    value.currency.toLowerCase() === quote.currency.toLowerCase();
  if (
    !nonzero(quote.quote_id) ||
    !nonzero(review.fee_quote_id) ||
    !money(review.bw_commission) ||
    !money(review.acquisition_fee) ||
    (review.provider_processing_charge != null &&
      !money(review.provider_processing_charge))
  )
    return false;
  const resolved = Date.parse(review.resolved_at);
  const bound = Date.parse(review.valid_until);
  const rawBound = Date.parse(quote.expires_at);
  return (
    Number.isFinite(resolved) &&
    Number.isFinite(bound) &&
    Number.isFinite(rawBound) &&
    resolved < bound &&
    now < bound &&
    now < rawBound
  );
}

/** Presentation freshness only; actual source/payment admission stays server-owned. */
export function useCommercialReviewReady(
  quote: EnrollmentModeQuoteResponse | undefined,
): boolean {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    setNow(Date.now());
    const bound = Math.min(
      Date.parse(quote?.expires_at ?? ""),
      Date.parse(quote?.commercial_fee?.valid_until ?? ""),
    );
    if (!Number.isFinite(bound) || bound <= Date.now()) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(bound - Date.now() + 1, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [quote]);
  return hasReadyCommercialReview(quote, Math.max(now, Date.now()));
}

export function CommercialFeeReview({
  quote,
  required = false,
}: {
  quote: EnrollmentModeQuoteResponse | undefined;
  required?: boolean;
}) {
  const review = quote?.commercial_fee;
  const ready = useCommercialReviewReady(quote);
  if (!review)
    return required ? (
      <Alert severity="warning">
        Payment review unavailable. Refresh before paying.
      </Alert>
    ) : null;
  if (review.readiness === "blocked")
    return (
      <Alert severity="warning">
        {Array.from(
          new Set(
            review.blocker_codes.map(
              (code) =>
                blockers[code] ??
                "Payment review unavailable. Refresh before paying.",
            ),
          ),
        ).join(" ")}
      </Alert>
    );
  if (!ready)
    return (
      <Alert severity="warning">
        Payment review expired or unavailable. Refresh before paying.
      </Alert>
    );
  // Minor-unit conversion is display formatting, never a fee or charge calculation.
  const minor = (amount: NonNullable<typeof review.bw_commission>) =>
    formatMoney(amount.amount_minor / 100, amount.currency);
  return (
    <Stack spacing={0.5} aria-label="Merchant fee disclosure">
      <Typography variant="body2">
        Merchant fees — not added to your payment.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        BW commission: {minor(review.bw_commission!)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Acquisition fee: {minor(review.acquisition_fee!)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Provider processing:{" "}
        {review.provider_processing_charge == null
          ? "Unknown"
          : minor(review.provider_processing_charge)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Review valid until {new Date(review.valid_until!).toLocaleString()}.
      </Typography>
    </Stack>
  );
}
